// src/types/audio.types.ts

// src/types/audio.types.ts

export enum DatasetOrigin {
  CIRCOR_1_0_3 = 'CIRCOR_1_0_3'
}

export enum AudioFocus {
  AORTIC = 'AV',
  PULMONIC = 'PV',
  MITRAL = 'MV',
  TRICUSPID = 'TV',
  PHC = 'Phc',
  UNKNOWN = 'UNKNOWN'
}

export enum AudioStatus {
  READY = 'READY',
  MISSING_FILE = 'MISSING_FILE',
  ERROR_METADATA = 'ERROR_METADATA'
}

export enum AgeGroup {
  NEONATE = 'Neonate',         // Recém-nascido (0-27 dias)
  INFANT = 'Infant',           // Bebê (28 dias - 1 ano)
  CHILD = 'Child',             // Criança (1-11 anos)
  ADOLESCENT = 'Adolescent',   // Adolescente (12-18 anos)
  YOUNG_ADULT = 'Young Adult', // Adulto jovem (19-21 anos)
  UNKNOWN = 'UNKNOWN'
}

export enum BiologicalSex {
  MALE = 'Male',
  FEMALE = 'Female',
  UNKNOWN = 'UNKNOWN'
}

export enum SignalQuality {
  EXCELLENT = 'EXCELLENT',
  GOOD = 'GOOD',
  ACCEPTABLE = 'ACCEPTABLE',
  POOR = 'POOR',
  UNKNOWN = 'UNKNOWN'
}

export enum MurmurType {
  PRESENT = 'Present',
  ABSENT = 'Absent',
  UNKNOWN = 'Unknown'
}

export enum MurmurTiming {
  EARLY_SYSTOLIC = 'Early-systolic',
  MID_SYSTOLIC = 'Mid-systolic',
  LATE_SYSTOLIC = 'Late-systolic',
  HOLOSYSTOLIC = 'Holosystolic',
  EARLY_DIASTOLIC = 'Early-diastolic',
  MID_DIASTOLIC = 'Mid-diastolic',
  LATE_DIASTOLIC = 'Late-diastolic',
  HOLODIASTOLIC = 'Holodiastolic'
}

export enum MurmurShape {
  CRESCENDO = 'Crescendo',
  DECRESCENDO = 'Decrescendo',
  DIAMOND = 'Diamond',
  PLATEAU = 'Plateau'
}

export enum MurmurGrading {
  // Graus sistólicos (escala I/VI)
  GRADE_I_VI = 'I/VI',
  GRADE_II_VI = 'II/VI',
  GRADE_III_VI = 'III/VI',
  GRADE_IV_VI = 'IV/VI',
  GRADE_V_VI = 'V/VI',
  GRADE_VI_VI = 'VI/VI',
  // Graus diastólicos (escala I/IV)
  GRADE_I_IV = 'I/IV',
  GRADE_II_IV = 'II/IV',
  GRADE_III_IV = 'III/IV'
}

export enum MurmurPitch {
  LOW = 'Low',
  MEDIUM = 'Medium',
  HIGH = 'High'
}

export enum MurmurQuality {
  BLOWING = 'Blowing',
  HARSH = 'Harsh',
  MUSICAL = 'Musical',
  RUMBLING = 'Rumbling'
}

export enum Outcome {
  NORMAL = 'Normal',
  ABNORMAL = 'Abnormal',
  UNKNOWN = 'Unknown'
}

export enum Campaign {
  CC2014 = 'CC2014',
  CC2015 = 'CC2015',
  UNKNOWN = 'UNKNOWN'
}

/**
 * Interface completa de metadados de áudio do CirCor
 * Inclui TODOS os 22+ campos do CSV
 */
export interface AudioMetadata {
  // Identificação
  audioId: string;
  subject_id: string;
  additional_id: string | null;
  
  // Metadados técnicos
  gcs_path: string;
  dataset_origin: DatasetOrigin;
  sample_rate_hz: number;
  duration_s: number;
  bit_depth?: number;
  channels?: number;
  tsv_path?: string;
  checksum_md5?: string;
  ingested_at: Date;
  status: AudioStatus;
  signal_quality: SignalQuality;
  tags?: string[];
  
  // Dados demográficos
  age: string | null;          // Faixa etária do CSV (Neonate, Infant, Child, Adolescent, Adult)
  age_group: AgeGroup;
  biological_sex: BiologicalSex;
  height_cm: number | null;
  weight_kg: number | null;
  pregnancy_status: boolean | null;
  
  // Localização e foco
  focus: AudioFocus;
  recording_locations: string;
  
  // Murmúrio geral
  murmur: MurmurType;
  murmur_locations: string | null;
  most_audible_location: string | null;
  
  // Murmúrio sistólico
  systolic_murmur_timing: MurmurTiming | null;
  systolic_murmur_shape: MurmurShape | null;
  systolic_murmur_grading: MurmurGrading | null;
  systolic_murmur_pitch: MurmurPitch | null;
  systolic_murmur_quality: MurmurQuality | null;
  
  // Murmúrio diastólico
  diastolic_murmur_timing: MurmurTiming | null;
  diastolic_murmur_shape: MurmurShape | null;
  diastolic_murmur_grading: MurmurGrading | null;
  diastolic_murmur_pitch: MurmurPitch | null;
  diastolic_murmur_quality: MurmurQuality | null;
  
  // Resultado e campanha
  outcome: Outcome;
  campaign: Campaign;
  
  // Label original (para compatibilidade)
  label_original: string;
  
  // Label atual do curador (opcional)
  current_label?: {
    label: string;
    comment?: string;
    author_name: string;
    created_at: Date;
  };
}

/**
 * Interface de filtros para busca de áudios
 */
export interface AudioFilters {
  // Filtros básicos
  focus?: AudioFocus[];
  age_group?: AgeGroup[];
  biological_sex?: BiologicalSex[];
  signal_quality?: SignalQuality[];
  status?: AudioStatus[];
  subject_id?: string;
  dataset_origin?: DatasetOrigin;
  
  // Filtros demográficos
  age_min?: number;
  age_max?: number;
  height_min?: number;
  height_max?: number;
  weight_min?: number;
  weight_max?: number;
  pregnancy_status?: boolean | null;  // CORRIGIDO: boolean | null ao invés de boolean[]
  
  // Filtros de murmúrio
  murmur?: MurmurType[];
  murmur_locations?: string;
  most_audible_location?: string[];
  
  // Filtros de murmúrio sistólico
  systolic_murmur_timing?: MurmurTiming[];
  systolic_murmur_shape?: MurmurShape[];
  systolic_murmur_grading?: MurmurGrading[];
  systolic_murmur_pitch?: MurmurPitch[];
  systolic_murmur_quality?: MurmurQuality[];
  
  // Filtros de murmúrio diastólico
  diastolic_murmur_timing?: MurmurTiming[];
  diastolic_murmur_shape?: MurmurShape[];
  diastolic_murmur_grading?: MurmurGrading[];
  diastolic_murmur_pitch?: MurmurPitch[];
  diastolic_murmur_quality?: MurmurQuality[];
  
  // Filtros de outcome e campanha
  outcome?: Outcome[];
  campaign?: Campaign[];
}

/**
 * Interface estendida com signed URL temporária
 */
export interface AudioWithUrl extends AudioMetadata {
  signedUrl?: string;
  signedUrlExpiry?: Date;
}

// ==================== LABELS AMIGÁVEIS ====================

export const AudioFocusLabels: Record<AudioFocus, string> = {
  [AudioFocus.AORTIC]: 'Aórtico',
  [AudioFocus.PULMONIC]: 'Pulmonar',
  [AudioFocus.MITRAL]: 'Mitral',
  [AudioFocus.TRICUSPID]: 'Tricúspide',
  [AudioFocus.PHC]: 'Outro (Phc)',
  [AudioFocus.UNKNOWN]: 'UNKNOWN'
};

export const AgeGroupLabels: Record<AgeGroup, string> = {
  [AgeGroup.NEONATE]: 'Recém-nascido (Neonate)',
  [AgeGroup.INFANT]: 'Bebê (Infant)',
  [AgeGroup.CHILD]: 'Criança (Child)',
  [AgeGroup.ADOLESCENT]: 'Adolescente (Adolescent)',
  [AgeGroup.YOUNG_ADULT]: 'Adulto Jovem (Young Adult)',
  [AgeGroup.UNKNOWN]: 'Desconhecido'
};

export const BiologicalSexLabels: Record<BiologicalSex, string> = {
  [BiologicalSex.MALE]: 'Masculino',
  [BiologicalSex.FEMALE]: 'Feminino',
  [BiologicalSex.UNKNOWN]: 'Desconhecido'
};

export const OutcomeLabels: Record<Outcome, string> = {
  [Outcome.NORMAL]: 'Normal',
  [Outcome.ABNORMAL]: 'Anormal',
  [Outcome.UNKNOWN]: 'Desconhecido'
};

export const MurmurTypeLabels: Record<MurmurType, string> = {
  [MurmurType.PRESENT]: 'Presente',
  [MurmurType.ABSENT]: 'Ausente',
  [MurmurType.UNKNOWN]: 'Desconhecido'
};

export const MurmurTimingLabels: Record<MurmurTiming, string> = {
  [MurmurTiming.EARLY_SYSTOLIC]: 'Sistólico Precoce',
  [MurmurTiming.MID_SYSTOLIC]: 'Meso-sistólico',
  [MurmurTiming.LATE_SYSTOLIC]: 'Tele-sistólico',
  [MurmurTiming.HOLOSYSTOLIC]: 'Holossistólico',
  [MurmurTiming.EARLY_DIASTOLIC]: 'Diastólico Precoce',
  [MurmurTiming.MID_DIASTOLIC]: 'Meso-diastólico',
  [MurmurTiming.LATE_DIASTOLIC]: 'Tele-diastólico',
  [MurmurTiming.HOLODIASTOLIC]: 'Holodiastólico'
};

export const MurmurShapeLabels: Record<MurmurShape, string> = {
  [MurmurShape.CRESCENDO]: 'Crescendo',
  [MurmurShape.DECRESCENDO]: 'Decrescendo',
  [MurmurShape.DIAMOND]: 'Diamante',
  [MurmurShape.PLATEAU]: 'Platô'
};

export const MurmurPitchLabels: Record<MurmurPitch, string> = {
  [MurmurPitch.LOW]: 'Grave',
  [MurmurPitch.MEDIUM]: 'Médio',
  [MurmurPitch.HIGH]: 'Agudo'
};

export const MurmurQualityLabels: Record<MurmurQuality, string> = {
  [MurmurQuality.BLOWING]: 'Sopro',
  [MurmurQuality.HARSH]: 'Áspero',
  [MurmurQuality.MUSICAL]: 'Musical',
  [MurmurQuality.RUMBLING]: 'Ruflar'
};

export const MurmurGradingLabels: Record<MurmurGrading, string> = {
  [MurmurGrading.GRADE_I_VI]: 'Grau I/VI (Sistólico)',
  [MurmurGrading.GRADE_II_VI]: 'Grau II/VI (Sistólico)',
  [MurmurGrading.GRADE_III_VI]: 'Grau III/VI (Sistólico)',
  [MurmurGrading.GRADE_IV_VI]: 'Grau IV/VI (Sistólico)',
  [MurmurGrading.GRADE_V_VI]: 'Grau V/VI (Sistólico)',
  [MurmurGrading.GRADE_VI_VI]: 'Grau VI/VI (Sistólico)',
  [MurmurGrading.GRADE_I_IV]: 'Grau I/IV (Diastólico)',
  [MurmurGrading.GRADE_II_IV]: 'Grau II/IV (Diastólico)',
  [MurmurGrading.GRADE_III_IV]: 'Grau III/IV (Diastólico)'
};

export const CampaignLabels: Record<Campaign, string> = {
  [Campaign.CC2014]: 'CC2014',
  [Campaign.CC2015]: 'CC2015',
  [Campaign.UNKNOWN]: 'Desconhecida'
};
